import React, { useMemo, useRef } from 'react';
import {
  Background,
  Controls,
  Edge,
  MiniMap,
  Node,
  NodeTypes,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import { Box, useTheme } from '@mui/material';
import { TaskEditorValue, TaskFlowErrors } from '@features/Admin/Agent/taskFlow/domain/contracts';
import {
  buildTaskFlowCanvasGraph,
  TaskFlowCanvasEdge,
  TaskFlowCanvasNode,
} from '@features/Admin/Agent/taskFlow/utils/task-flow-mapper';
import { TaskFlowNode, TaskFlowNodeData } from './TaskFlowNode';
import 'reactflow/dist/style.css';

const X_SPACING = 260;
const X_START = 80;

type TaskFlowCanvasProps = {
  tasks: TaskEditorValue[];
  errors: TaskFlowErrors;
  selectedTaskKey: string | null;
  onSelectTask: (key: string) => void;
  onReorderTask: (fromIndex: number, toIndex: number) => void;
  disabled?: boolean;
  height?: number | string | { xs: number; sm: number };
};

const nodeTypes: NodeTypes = { taskFlowNode: TaskFlowNode };

const toReactFlowNodes = (
  nodes: TaskFlowCanvasNode[],
  errors: TaskFlowErrors,
): Node<TaskFlowNodeData>[] =>
  nodes.map((node) => ({
    id: node.id,
    position: node.position,
    type: 'taskFlowNode',
    data: {
      task: node.data.task,
      index: node.data.index,
      hasError: Boolean(errors.taskErrors[node.data.task.clientKey]),
    },
  }));

const toReactFlowEdges = (edges: TaskFlowCanvasEdge[], isDarkMode: boolean): Edge[] =>
  edges.map((edge) => ({
    ...edge,
    type: 'default',
    style: { strokeWidth: 1.75, stroke: isDarkMode ? '#64748b' : '#475467' },
  }));

export const TaskFlowCanvas: React.FC<TaskFlowCanvasProps> = ({
  tasks,
  errors,
  selectedTaskKey,
  onSelectTask,
  onReorderTask,
  disabled = false,
  height = 420,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isDraggingRef = useRef(false);
  const graph = useMemo(() => buildTaskFlowCanvasGraph(tasks), [tasks]);

  const [nodes, setNodes, onNodesChange] = useNodesState(toReactFlowNodes(graph.nodes, errors));
  const [edges, setEdges, onEdgesChange] = useEdgesState(toReactFlowEdges(graph.edges, isDarkMode));

  React.useEffect(() => {
    if (isDraggingRef.current) return;
    setNodes(toReactFlowNodes(graph.nodes, errors));
  }, [graph.nodes, errors, setNodes]);

  React.useEffect(() => {
    setEdges(toReactFlowEdges(graph.edges, isDarkMode));
  }, [graph.edges, isDarkMode, setEdges]);

  React.useEffect(() => {
    setNodes((prev) => prev.map((node) => ({ ...node, selected: node.id === selectedTaskKey })));
  }, [selectedTaskKey, setNodes]);

  return (
    <Box
      sx={{
        width: '100%',
        height,
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 1,
        backgroundColor: (t) => t.palette.background.default,
        overflow: 'hidden',
      }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={(_, node) => {
          onSelectTask(node.id);
        }}
        onNodeDragStart={() => {
          isDraggingRef.current = true;
        }}
        onNodeDragStop={(_, node) => {
          isDraggingRef.current = false;
          if (disabled) return;
          const currentIndex = graph.nodes.findIndex((n) => n.id === node.id);
          if (currentIndex === -1) return;
          const targetIndex = Math.max(
            0,
            Math.min(tasks.length - 1, Math.round((node.position.x - X_START) / X_SPACING)),
          );
          if (targetIndex !== currentIndex) {
            onReorderTask(currentIndex, targetIndex);
          } else {
            // Snap back to the canonical position for this order.
            setNodes(toReactFlowNodes(graph.nodes, errors));
          }
        }}
        nodesDraggable={!disabled}
        nodesConnectable={false}
        elementsSelectable
        panOnDrag
        fitView
        fitViewOptions={{ padding: 0.2, minZoom: 0.6, maxZoom: 1.2 }}
        minZoom={0.5}
        nodeTypes={nodeTypes}>
        <MiniMap
          pannable
          zoomable
          style={{
            backgroundColor: isDarkMode ? '#0b111d' : '#ffffff',
            border: `1px solid ${theme.palette.divider}`,
            width: 120,
            height: 80,
            borderRadius: 8,
          }}
        />
        <Controls />
        <Background
          color={isDarkMode ? 'rgba(148,163,184,0.2)' : 'rgba(0,0,0,0.06)'}
          gap={24}
          size={1}
        />
      </ReactFlow>
    </Box>
  );
};
